import DOMPurify from "dompurify";

export const cleanHTML = (html) => {
  // Convert newlines to <br> before sanitizing
  const normalized = html?.replace(/\n/g, "<br>");

  return DOMPurify.sanitize(normalized, {
    ALLOWED_TAGS: [
      "p",
      "br",
      "a",
      "strong",
      "b",
      "i",
      "em",
      "ins",
      "u",
      "ul",
      "ol",
      "li",
      "h1",
      "h2",
      "h3",
      "h4",
      "span",
      "blockquote",
    ],
    ALLOWED_ATTR: ["href", "target", "rel"],
    FORBID_TAGS: ["style", "script"],
    FORBID_ATTR: ["style", "class", "id", "onclick", "onerror"],
  });
};
export const cleanHTML2 = (html) => {
  if (!html) return "";

  let normalized = html
    .replace(/\r\n/g, "\n")
    .replace(/\n{2,}/g, "<br><br>") // paragraph breaks
    .replace(/([^\n])\n([^\n])/g, "$1 $2") // normal newlines removed
    .replace(/<\/(p|h1|h2|h3|h4|h5|h6|li)>\s*<br>/g, "</$1>"); // remove extra <br>

  return DOMPurify.sanitize(normalized, {
    ALLOWED_TAGS: [
      "p",
      "br",
      "a",
      "strong",
      "b",
      "i",
      "em",
      "ins",
      "u",
      "ul",
      "ol",
      "li",
      "h1",
      "h2",
      "h3",
      "h4",
      "h5",
      "h6",
      "span",
      "blockquote",
    ],
    ALLOWED_ATTR: ["href", "target", "rel"],
    FORBID_TAGS: ["style", "script"],
    FORBID_ATTR: ["style", "class", "id", "onclick", "onerror"],
  });
};
export const flattenHTML = (html) => {
  return html
    .replace(/<\/?p>/g, " ") // remove <p> tags
    .replace(/<\/?div>/g, " ") // remove <div> tags
    .replace(/<br\s*\/?>/g, " "); // optional: replace <br> with space
};

export const formatString = (str) => {
  if (!str) return "";

  return str
    .split("_") // split by underscores
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // capitalize each word
    .join(" "); // join words with a space
};

export const formatToLabelValue = (items) => {
  if (!Array.isArray(items)) return [];
  return items.map((item) => ({
    label: item.name || "", // use value as label
    value: item._id || "", // use _id as value
  }));
};
export const formatToSameLabelValue = (items) => {
  if (!Array.isArray(items)) return [];
  return items.map((item) => ({
    label: item.name || "", // use value as label
    value: item.name || "", // use _id as value
  }));
};
export const formatToTitleValue = (items) => {
  if (!Array.isArray(items)) return [];
  return items.map((item) => ({
    label: item.title || "", // use value as label
    value: item._id || "", // use _id as value
  }));
};
export const isTrial = (text, exact = false) => {
  if (!text || typeof text !== "string") return false;
  const lower = text.trim().toLowerCase();
  if (exact) {
    return lower === "trial"; // exact match only
  }
  // Check if 'trial' appears as a separate word or prefix
  return /\btrial\b/i.test(text);
};

export function formatDate(isoDate) {
  if (!isoDate) return "";
  const date = new Date(isoDate);
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}
export function formatDateTime(isoDate) {
  if (!isoDate) return "";
  const date = new Date(isoDate);
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

export const is48HoursOrMore = (dateString) => {
  const target = new Date(dateString);
  const now = new Date();

  // Normalize both dates to midnight (00:00:00)
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const targetDay = new Date(
    target.getFullYear(),
    target.getMonth(),
    target.getDate(),
  );

  // Difference in days
  const diffDays = (targetDay - today) / (1000 * 60 * 60 * 24);

  // Editable ONLY if day is 2 or more days ahead
  return diffDays >= 2;
};

const isEditableDate = (dateString) => {
  const target = new Date(dateString);
  const now = new Date();

  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const targetDay = new Date(
    target.getFullYear(),
    target.getMonth(),
    target.getDate(),
  );

  const diffDays = (targetDay - today) / (1000 * 60 * 60 * 24);

  return diffDays >= 2; // editable if day after tomorrow or later
};

export const isMealCustomizationPending = (data) => {
  if (
    !data?.remainingDaysDates ||
    !data?.selectedMeals ||
    !data?.subscribedMeals
  )
    return true;

  const subscribedMeals = data.subscribedMeals.map((m) => m?.meal?._id);

  const normalizeDate = (str) => {
    const d = new Date(str);
    return d.toISOString().split("T")[0];
  };

  const hasPending = data.remainingDaysDates.some((d) => {
    if (!isEditableDate(d.date)) return false;

    const dateStr = normalizeDate(d.date);

    const mealsForDate = data.selectedMeals
      .filter((m) => normalizeDate(m.date) === dateStr)
      .map((m) => m.meal._id);

    return !subscribedMeals.every((mealId) => mealsForDate.includes(mealId));
  });

  return hasPending;
};

export const isDayPassed = (dateString) => {
  if (!dateString) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0); // Normalize to start of the day

  const targetDate = new Date(dateString);
  targetDate.setHours(0, 0, 0, 0);

  return targetDate < today; // true if the date is before today
};
export const isDayPassedOrToday = (dateString) => {
  if (!dateString) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0); // Normalize to start of the day

  const targetDate = new Date(dateString);
  targetDate.setHours(0, 0, 0, 0);

  return targetDate <= today; // true if the date is before today
};
export const getFoodByDateAndTime = (foodData, date, time) => {
  if (!foodData || !Array.isArray(foodData)) return [];

  const formattedDate = date?.trim().toLowerCase();
  const formattedTime = time?.trim().toLowerCase();

  const match = foodData.find(
    (f) =>
      f.date?.trim().toLowerCase() === formattedDate &&
      f.mealId?.trim().toLowerCase() === formattedTime,
  );

  if (!match || !Array.isArray(match.availableFoods)) return [];
  // console.log(
  //   match.availableFoods.map((food) => ({
  //     value: food._id,
  //     label: food.title,
  //   }))
  // );
  return match.availableFoods.map((food) => ({
    _id: food._id,
    title: food.title,
    vegOrNonVeg: food.vegOrNonVeg,
    foodTime: food.foodTime,
    allergens: food.allergens,
    hotOrCold: food.hotOrCold,
    description: food.description,
    image: food.image,
    nutritionalInfo: food.nutritionalInfo,
    ingredients: food.ingredients,
    pictures: food.pictures,
  }));
};
export const getDefaultFoodByDateAndTime = (foodData, date, time) => {
  if (!foodData || !Array.isArray(foodData)) return [];

  const formattedDate = date?.trim().toLowerCase();
  const formattedTime = time?.trim().toLowerCase();

  const match = foodData.find(
    (f) =>
      f.date?.trim().toLowerCase() === formattedDate &&
      f.mealId?.trim().toLowerCase() === formattedTime,
  );

  if (!match || !match.defaultFood) return [];
  // console.log(
  //   match.availableFoods.map((food) => ({
  //     value: food._id,
  //     label: food.title,
  //   }))
  // );
  return match.defaultFood?.title;
};
export const getDefaultFoodObjectByDateAndTime = (foodData, date, time) => {
  if (!foodData || !Array.isArray(foodData)) return [];

  const formattedDate = date?.trim().toLowerCase();
  const formattedTime = time?.trim().toLowerCase();

  const match = foodData.find(
    (f) =>
      f.date?.trim().toLowerCase() === formattedDate &&
      f.mealId?.trim().toLowerCase() === formattedTime,
  );

  if (!match || !match.defaultFood) return [];
  // console.log(
  //   match.availableFoods.map((food) => ({
  //     value: food._id,
  //     label: food.title,
  //   }))
  // );
  return {
    meal: { title: match.defaultFood?.foodTime },
    food: match.defaultFood,
  };
};
// export const isMealCustomizationPendingInDetails = (data) => {
//   if (
//     !data?.subscribedMeals ||
//     !data?.selectedMeals ||
//     !data?.subscriptionStartDate ||
//     !data?.subscriptionEndDate
//   ) {
//     return true; // pending if data missing
//   }

//   const subscribedMealIds = data.subscribedMeals.map((m) => m?.meal?._id);

//   const normalizeDate = (str) => {
//     const d = new Date(str);
//     return d.toISOString().split("T")[0];
//   };

//   const today = new Date();
//   today.setHours(0, 0, 0, 0);
//   const tomorrow = new Date(today);
//   tomorrow.setDate(today.getDate() + 1);

//   // paused date
//   const pausedDate = data.pausedOneTime && data.paused?.pauseDate ? new Date(data.paused.pauseDate) : null;

//   // Helper: get Monday of a week
//   const getMonday = (d) => {
//     const date = new Date(d);
//     const day = date.getDay();
//     const diff = day === 0 ? -6 : 1 - day; // Sunday=0
//     date.setDate(date.getDate() + diff);
//     date.setHours(0, 0, 0, 0);
//     return date;
//   };

//   // last week and current week range
//   const lastWeekMonday = getMonday(new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000));
//   const currentWeekMonday = getMonday(today);
//   const currentWeekSunday = new Date(currentWeekMonday);
//   currentWeekSunday.setDate(currentWeekMonday.getDate() + 6);

//   // loop through last week + current week
//   for (let d = new Date(lastWeekMonday); d <= currentWeekSunday; d.setDate(d.getDate() + 1)) {
//     const dateStr = normalizeDate(d);

//     // skip Sat(6)/Sun(0)
//     const dayOfWeek = d.getDay();
//     if (dayOfWeek === 0 || dayOfWeek === 6) continue;

//     // skip past, today, tomorrow, paused date
//     if (
//       d < today ||
//       dateStr === normalizeDate(today) ||
//       dateStr === normalizeDate(tomorrow) ||
//       (pausedDate && dateStr === normalizeDate(pausedDate))
//     ) {
//       continue;
//     }

//     // selected meals for this date
//     const mealsForDate = data.selectedMeals
//       .filter((m) => normalizeDate(m.date) === dateStr)
//       .map((m) => m.meal._id);

//     if (!subscribedMealIds.every((mealId) => mealsForDate.includes(mealId))) {
//       return true; // pending
//     }
//   }

//   return false; // all required dates have meals selected
// };

export const isMealCustomizationPendingInDetails = (data) => {
  if (
    !data?.subscribedMeals ||
    !data?.selectedMeals ||
    !data?.subscriptionStartDate ||
    !data?.subscriptionEndDate
  ) {
    return true; // pending if data missing
  }

  const subscribedMealIds = data.subscribedMeals.map((m) => m?.meal?._id);

  const normalizeDate = (str) => {
    const d = new Date(str);
    return d.toISOString().split("T")[0];
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);

  // paused date
  const pausedDate =
    data.pausedOneTime && data.paused?.pauseDate
      ? new Date(data.paused.pauseDate)
      : null;

  // Helper: get Monday of a week
  const getMonday = (d) => {
    const date = new Date(d);
    const day = date.getDay();
    const diff = day === 0 ? -6 : 1 - day; // Sunday=0
    date.setDate(date.getDate() + diff);
    date.setHours(0, 0, 0, 0);
    return date;
  };

  // This week and next week
  const thisWeekMonday = getMonday(today);
  const nextWeekMonday = new Date(thisWeekMonday);
  nextWeekMonday.setDate(thisWeekMonday.getDate() + 7);

  const thisWeekSunday = new Date(thisWeekMonday);
  thisWeekSunday.setDate(thisWeekMonday.getDate() + 6);
  const nextWeekSunday = new Date(nextWeekMonday);
  nextWeekSunday.setDate(nextWeekMonday.getDate() + 6);

  // Subscription end date
  const subscriptionEnd = new Date(data.subscriptionEndDate);
  subscriptionEnd.setHours(0, 0, 0, 0);

  // Loop through this week + next week
  for (
    let d = new Date(thisWeekMonday);
    d <= nextWeekSunday && d <= subscriptionEnd;
    d.setDate(d.getDate() + 1)
  ) {
    const dateStr = normalizeDate(d);

    // skip Sat(6)/Sun(0)
    const dayOfWeek = d.getDay();
    if (dayOfWeek === 0 || dayOfWeek === 6) continue;

    // skip past, today, tomorrow, paused date
    if (
      d < today ||
      dateStr === normalizeDate(today) ||
      dateStr === normalizeDate(tomorrow) ||
      (pausedDate && dateStr === normalizeDate(pausedDate))
    ) {
      continue;
    }

    // selected meals for this date
    const mealsForDate = data.selectedMeals
      .filter((m) => normalizeDate(m.date) === dateStr)
      .map((m) => m.meal._id);

    if (!subscribedMealIds.every((mealId) => mealsForDate.includes(mealId))) {
      return true; // pending
    }
  }

  return false; // all required dates have meals selected
};

export const isAllowedToReturn = (orderDate) => {
  if (!orderDate) return false;

  const allowedDays = parseInt(
    process.env.REACT_APP_PRODUCT_RETURN_WINDOW_DAYS,
  );

  const order = new Date(orderDate);
  const now = new Date();

  const diffTime = now - order;
  const diffDays = diffTime / (1000 * 60 * 60 * 24);

  return diffDays < allowedDays;
};

export const getRemainingDays = (startDate, count) => {
  const today = new Date();
  const givenDate = new Date(startDate);

  // Calculate difference in milliseconds
  const diffTime = today - givenDate;

  // Convert to days (round down)
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  // Subtract passed days from count
  const remaining = count - diffDays;

  // Return remaining days (minimum 0)
  return remaining > 0 ? remaining : 0;
};

export const anyPlanHasPriceBadgeText = (data) => {
  if (!data || !Array.isArray(data)) return false;

  return data.some(
    (plan) =>
      typeof plan.priceBadgeText === "string" &&
      plan.priceBadgeText.trim() !== "",
  );
};
export const getHowManyDaysRemaining = (targetDate) => {
  const today = new Date();
  const future = new Date(targetDate);

  // Reset time for fair comparison (ignore hours/min/sec)
  today.setHours(0, 0, 0, 0);
  future.setHours(0, 0, 0, 0);

  const diffTime = future - today; // future - now

  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return diffDays > 0 ? diffDays : 0; // never return negative
};

// export const hasPassedMoreThanHalf = (startDate, totalDays) => {
//   const start = new Date(startDate);
//   const today = new Date();

//   start.setHours(0, 0, 0, 0);
//   today.setHours(0, 0, 0, 0);

//   let passedDays = 0;
//   const date = new Date(start);

//   // Count weekdays between start and today
//   while (date <= today) {
//     const day = date.getDay(); // 0 = Sun, 6 = Sat
//     if (day !== 0 && day !== 6) passedDays++;
//     date.setDate(date.getDate() + 1);
//   }

//   // Remove the start day from count if needed
//   passedDays = Math.max(passedDays - 1, 0);

//   // Percentage thresholds logic
//   let percentage = 0.5; // default 50%

//   if (totalDays === 5)
//     percentage = 0.4; // 50%
//   else if (totalDays === 10)
//     percentage = 0.6; // 60%
//   else if (totalDays === 20 || totalDays > 20) percentage = 0.7; // 70%
//   console.log(passedDays, totalDays);
//   return passedDays >= totalDays * percentage;
// };
export const isSubscriptionRenewable = (startDate) => {
  const start = new Date(startDate);
  const today = new Date();
  start.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  return start <= today;
};
export const getPausedPeriodText = (data) => {
  if (!data?.paused) return "No paused data available";

  const { pauseDate, reactivationDate } = data.paused;

  const start = new Date(pauseDate);
  const end = new Date(reactivationDate);

  const diffTime = end - start;
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

  // Singular/plural day/days
  const dayText = diffDays === 1 ? "Day" : "Days";

  const formatDate = (date) =>
    date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  if (diffDays > 0) {
    return `Paused Period - ${diffDays} ${dayText} (From ${formatDate(
      start,
    )} - To ${formatDate(end)})`;
  }
};

export const isSnack = (str) => {
  if (!str) return false;
  const snacks = [
    "snack",
    "snacks",
    "afternoon snack",
    "afternoon snacks",
    "afternoon-snacks",
    "afternoon-snack",
    "evening snack",
    "evening-snack",
  ];
  return snacks.some((s) => s.toLowerCase() === str.toLowerCase().trim());
};

export const getPausedDates = (startDate, endDate) => {
  const start = parseLongDate(startDate);
  const end = parseLongDate(endDate);

  start.setDate(start.getDate() + 2); // +48h
  end.setDate(end.getDate() + 1); // +1 day

  const dates = [];
  let current = start;

  while (current <= end) {
    const day = current.getDay(); // 0=Sun, 6=Sat

    if (day !== 0 && day !== 6) {
      dates.push(formatLongDate(current)); // 👈 formatted string
    }

    current.setDate(current.getDate() + 1);
  }

  return dates;
};

// parse "08 December 2025"
export const parseLongDate = (str) => {
  const [day, monthName, year] = str.split(" ");
  const month = new Date(`${monthName} 1, 2000`).getMonth();
  return new Date(year, month, parseInt(day));
};

// convert Date object to "10 December 2025"
export const formatLongDate = (date) => {
  const day = String(date.getDate()).padStart(2, "0");
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

// check if formatted date exists in the array
export const includesLongDate = (dateArray, longDateStr) => {
  const [day, monthName, year] = longDateStr.split(" ");
  const month = new Date(`${monthName} 1, 2000`).getMonth();

  return dateArray.some((d) => {
    const date = parseLongDate(d);
    return (
      date.getDate() === parseInt(day) &&
      date.getMonth() === month &&
      date.getFullYear() === parseInt(year)
    );
  });
};

export const hasDefaultFoodForDate = (foods, date) => {
  if (!Array.isArray(foods)) return false;

  return foods.some(
    (item) => item.date === date && item.defaultFood && item.defaultFood.title,
  );
};

export const allMealsSelectedForDate = (subscription, date) => {
  const subscribedMeals = subscription.subscribedMeals.map((m) => m.meal._id);

  // All selected meal IDs for this date
  const selectedMealIds = subscription.selectedMeals
    .filter((m) => m.date === date)
    .map((m) => m.meal._id);

  // Check if EVERY subscribed meal is selected for this date
  return subscribedMeals.every((mealId) => selectedMealIds.includes(mealId));
};

export const validateMealSelectionAndCityInCart = (data, validation) => {
  const mealCart = data?.mealCart ?? {};

  const city = mealCart.shippingAddress?.city;
  const mealIdsCount = mealCart.mealIds?.length ?? 0;

  // If city not selected, don't block
  if (!city) return true;

  if (city === "Dubai") {
    return (
      mealIdsCount >=
      (validation?.mealSubscriptionMinimumItemRequirement?.dubai ?? 1)
    );
  }

  if (city === "Abu Dhabi") {
    return (
      mealIdsCount >=
      (validation?.mealSubscriptionMinimumItemRequirement?.abuDhabi ?? 1)
    );
  }

  if (city === "Sharjah") {
    return (
      mealIdsCount >=
      (validation?.mealSubscriptionMinimumItemRequirement?.sharjah ?? 1)
    );
  }

  if (city === "Ajman") {
    return (
      mealIdsCount >=
      (validation?.mealSubscriptionMinimumItemRequirement?.ajman ?? 1)
    );
  }

  if (city === "Fujairah") {
    return (
      mealIdsCount >=
      (validation?.mealSubscriptionMinimumItemRequirement?.fujairah ?? 1)
    );
  }

  if (city === "Umm Al Quwain") {
    return (
      mealIdsCount >=
      (validation?.mealSubscriptionMinimumItemRequirement?.ummAlQuwain ?? 1)
    );
  }

  if (city === "Ras Al Khaimah") {
    return (
      mealIdsCount >=
      (validation?.mealSubscriptionMinimumItemRequirement?.rasAlKhaimah ?? 1)
    );
  }
};

export const getMinimumMealCountByCity = (data, validation) => {
  const mealCart = data?.mealCart ?? {};
  const city = mealCart.shippingAddress?.city;

  // If city not selected, don't block
  if (!city) return 0;

  if (city === "Dubai") {
    return validation?.mealSubscriptionMinimumItemRequirement?.dubai ?? 1;
  }

  if (city === "Abu Dhabi") {
    return validation?.mealSubscriptionMinimumItemRequirement?.abuDhabi ?? 1;
  }

  if (city === "Sharjah") {
    return validation?.mealSubscriptionMinimumItemRequirement?.sharjah ?? 1;
  }

  if (city === "Ajman") {
    return validation?.mealSubscriptionMinimumItemRequirement?.ajman ?? 1;
  }

  if (city === "Fujairah") {
    return validation?.mealSubscriptionMinimumItemRequirement?.fujairah ?? 1;
  }

  if (city === "Umm Al Quwain") {
    return validation?.mealSubscriptionMinimumItemRequirement?.ummAlQuwain ?? 1;
  }

  if (city === "Ras Al Khaimah") {
    return (
      validation?.mealSubscriptionMinimumItemRequirement?.rasAlKhaimah ?? 1
    );
  }

  return 3;
};

export const anyUnavailableDeliveryCities = (validation) => {
  const deliveryAvailability = validation?.mealDeliveryAvailable;

  if (!deliveryAvailability) return false;

  return Object.values(deliveryAvailability).some((value) => value === "0");
};
const formatCamelCase = (text) =>
  text
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/^./, (char) => char.toUpperCase());

export const getUnavailableDeliveryCities = (validation) => {
  const availability = validation?.mealDeliveryAvailable;

  if (!availability) return "";

  return Object.entries(availability)
    .filter(([_, value]) => value === "0")
    .map(([city]) => formatCamelCase(city))
    .join(", ");
};

export const CITY_OPTIONS = [
  { label: "Abu Dhabi", value: "Abu Dhabi" },
  { label: "Ajman", value: "Ajman" },
  { label: "Dubai", value: "Dubai" },
  { label: "Fujairah", value: "Fujairah" },
  { label: "Ras Al Khaimah", value: "Ras Al Khaimah" },
  { label: "Sharjah", value: "Sharjah" },
  { label: "Umm Al Quwain", value: "Umm Al Quwain" },
];
export const CITY_KEY_MAP = {
  "Abu Dhabi": "abuDhabi",
  Ajman: "ajman",
  Dubai: "dubai",
  Fujairah: "fujairah",
  "Ras Al Khaimah": "rasAlKhaimah",
  Sharjah: "sharjah",
  "Umm Al Quwain": "ummAlQuwain",
};

export const filterAvailableCityOptions = (options, validation) => {
  // const availability = {
  //   dubai: "1",
  //   ummAlQuwain: "0",
  //   rasAlKhaimah:"0",
  // };
  const availability = validation?.mealDeliveryAvailable;
  if (!availability) return options;

  return options.filter(
    (option) => availability[CITY_KEY_MAP[option.value]] !== "0",
  );
};

export const isCityDeliverable = (data, validation) => {
  const mealCart = data?.mealCart ?? {};

  const city = mealCart.shippingAddress?.city;
  const availability = validation?.mealDeliveryAvailable;
  if (!availability || !city) return true;

  const cityKey = CITY_KEY_MAP[city] ?? city;

  // deliverable if value is NOT 0
  return availability[cityKey] !== "0";
};

export const formatDateToReadable = (dateString) => {
  if (!dateString) return "";

  const date = new Date(dateString);

  return date.toLocaleDateString("en-GB", {
    day: "2-digit", // 👈 adds leading 0
    month: "long",
    year: "numeric",
  });
};

export const replaceWithCountAndCity = (cart, extraContent, message) => {
  const city = cart?.mealCart?.shippingAddress?.city;
  const modalMessage = message || extraContent?.minimumMealRequiredMessages?.modalMessage;
  
  if (!modalMessage || !city) return modalMessage || '';
  
  // Convert city name to camelCase key (e.g., "Abu Dhabi" → "abuDhabi")
  const cityKey = city
    .split(' ')
    .map((word, index) => 
      index === 0 
        ? word.toLowerCase() 
        : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    )
    .join('');
  
  const minMealRequired = extraContent?.mealSubscriptionMinimumItemRequirement?.[cityKey];
  
  return modalMessage
    .replace('{city}', city)
    .replace('{minMealRequired}', minMealRequired || '');
};